import {
  emailRefusal,
  renderEmailTemplate,
  splitRecipients,
  unresolvedEmailTokens,
  type ConnectedSender,
  type EmailableContact,
  type EmailTemplate,
} from "@roof-hawk/core";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { GmailError, sendEmail } from "../../lib/gmail";
import {
  AttachmentPicker,
  toAttachments,
  type PickedFile,
} from "./Attachments";

/**
 * Writing and sending one email.
 *
 * Used by every place in the app that sends something — the Emails card on a
 * job, the production handoff, the estimate a customer has to sign — so that
 * they all behave the same way when Gmail is not connected, when the customer
 * has asked not to be contacted, and when a template leaves a merge field it
 * could not fill.
 *
 * Two things it does deliberately:
 *
 *   **Copy to clipboard never goes away.** It was the only way to send before
 *   Gmail was connected and it stays as the way out of everything that can go
 *   wrong afterwards — an expired connection, a Google outage, a rep who has
 *   not connected yet. A send button that fails and leaves someone with no
 *   route to the customer would be worse than the clipboard it replaced.
 *
 *   **The sender can be somebody else.** Jonathon, 2026-08-24: "if I want to
 *   send an estimate for samuel i should be able to select samuel as the
 *   sender." The picker only appears when there is a choice — a rep has one
 *   mailbox and sees a line of text instead — and the list only ever holds
 *   people who have actually connected Google. What the browser asks for is
 *   not what decides it: `resolveEmailSender` inside the Edge Function is.
 *
 *   **An unfilled merge field is shown before it is sent, not after.** The
 *   import left tokens like `{{job.claim.policy_holder_name}}` in the bodies it
 *   could not map (ADR 0029), and they render as themselves. That is the right
 *   choice — a visible placeholder gets noticed and a blank does not — but the
 *   place to notice is here, with the customer's name still on screen.
 */

export interface ComposeEmailProps {
  title: string;
  subtitle?: string;
  to: string;
  subject: string;
  /** Plain text, like every template in this system. */
  body: string;
  /** Offered in a picker. Already filtered to the right context by the caller. */
  templates?: readonly EmailTemplate[];
  /** Merge values for whichever template is picked. */
  values?: Readonly<Record<string, string>>;
  contact?: Partial<EmailableContact> | null | undefined;
  contactId?: string | null;
  jobId?: string | null;
  /** Staff to staff — logged as internal, and no contact flags apply. */
  internal?: boolean;
  /** Shown when this composer is open but nobody has connected Gmail. */
  connectedAs?: string | null;
  /**
   * The mailboxes this person may send from, from `useGmailSenders`.
   *
   * A prop rather than a hook call, like everything else here: this component
   * takes what it needs so it can be rendered on its own in a test. Empty or
   * absent means the old behaviour exactly — one mailbox, `connectedAs`, no
   * picker.
   */
  senders?: readonly ConnectedSender[] | undefined;
  /** The signed-in person's own mailbox, for saying where replies will land. */
  ownSender?: ConnectedSender | null;
  /**
   * Whose mailbox the From line starts on, when it is not the caller's own.
   *
   * The night-before email leaves from Lysa's (ADR 0313). Only a mailbox in
   * `senders` counts — anybody else's falls back to the caller's own, so a
   * rep who may not send as Lysa is never offered a send that would be
   * refused.
   */
  defaultFromProfileId?: string | null;
  /**
   * Told whose mailbox the person picked on the From line, for a caller that
   * closes this and opens it again — the night-before email goes Back to its
   * questions, and the mailbox picked must still be picked on the way forward.
   */
  onFromChange?: (profileId: string) => void;
  sendLabel?: string;
  /**
   * Files the caller puts on the letter, as opposed to the ones a person
   * picks. Built at the moment Send is pressed, not when the modal opens: a
   * work order's PDF is drawn from whatever the order says by then, and drawing
   * it up front would attach a copy of the order as it was before the last
   * edit. Listed on screen, so nobody has to send it to find out what went.
   */
  documents?: readonly ComposedDocument[];
  /**
   * Put above the To line. For the one question a particular letter has to ask
   * before it goes — whether the crew's pay is on their copy, so far.
   */
  extraFields?: ReactNode;
  /**
   * Put beside Copy and Send. The handoff uses it for "Mark as sent", which
   * has to stay reachable for whoever has not connected Gmail yet — otherwise
   * connecting would become a prerequisite for moving a job into production.
   */
  extraActions?: ReactNode;
  onClose: () => void;
  /**
   * Runs after Google has accepted the message, before the modal closes.
   *
   * Given who it actually went to and which mailbox it left from, because the
   * To line is editable: a caller recording "sent to the crew" has to record
   * the address on the letter, not the one it was opened with.
   */
  onSent?: (sent: { to: string; from: string }) => Promise<void> | void;
  /**
   * Runs when the letter is copied to be sent some other way, with the To
   * line as it stood, for a caller that records a letter as sent either way.
   */
  onCopied?: (copied: { to: string }) => Promise<void> | void;
  /**
   * Told the letter as it stands whenever the To line, subject or message
   * changes, for a caller that closes this and opens it again: the handoff
   * goes Back to its questions, and the rep's edits must be there when they
   * come forward.
   */
  onDraftChange?: (draft: EmailDraft) => void;
}

/** The parts of a letter a person types. */
export interface EmailDraft {
  to: string;
  subject: string;
  body: string;
}

/** A file the caller hangs off the letter. */
export interface ComposedDocument {
  /** What it is called in the recipient's inbox. */
  filename: string;
  mimeType: string;
  /** What it is, in words, for the line that says what is attached. */
  label?: string;
  /** The bytes, base64, worked out when Send is pressed. */
  build: () => Promise<string>;
}

export function ComposeEmail({
  title,
  subtitle,
  to: initialTo,
  subject: initialSubject,
  body: initialBody,
  templates,
  values,
  contact,
  contactId,
  jobId,
  internal,
  connectedAs,
  senders = [],
  ownSender = null,
  defaultFromProfileId = null,
  onFromChange,
  sendLabel = "Send",
  documents = [],
  extraFields,
  extraActions,
  onClose,
  onSent,
  onCopied,
  onDraftChange,
}: ComposeEmailProps) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [files, setFiles] = useState<PickedFile[]>([]);

  // A ref, so a caller passing a new function each render does not make this
  // run on every render.
  const draftChanged = useRef(onDraftChange);
  draftChanged.current = onDraftChange;
  useEffect(() => {
    draftChanged.current?.({ to, subject, body });
  }, [to, subject, body]);

  /*
   * Whose mailbox this leaves from.
   *
   * Starts on the signed-in person's own — the ordinary answer, and the first
   * entry `sendableAs` returns. Somebody with no connection of their own but
   * the right to send from others (an admin who has not connected Google) gets
   * the first mailbox in the list rather than nothing, which is what makes the
   * feature usable for the person most likely to want it.
   *
   * A caller may name a different mailbox to start on — a letter that is
   * Lysa's to send starts on hers — and a name not in the list is ignored.
   */
  const own = ownSender;
  const [fromProfileId, setFromProfileId] = useState<string | null>(
    defaultFromProfileId,
  );
  const sender =
    senders.find((row) => row.profileId === fromProfileId) ??
    senders[0] ??
    null;
  /*
   * What is on the From line. The prop is what the caller knew when it opened
   * this — the signed-in person's address — and the picker overrides it.
   * Falling back to the prop keeps every existing caller working unchanged,
   * including the ones rendered in a test with no session behind them.
   */
  const sendingAs = sender?.emailAddress ?? connectedAs ?? null;

  // The flags belong to the contact, so a staff email is not subject to them.
  // The server checks this again either way; this is what greys the button and
  // says why, which the server cannot do.
  const refusal = internal ? null : emailRefusal(contact ?? null, to);
  // What is actually in the box, so the count below and the button agree with
  // what the Edge Function will do with the same string.
  const recipients = splitRecipients(to);
  const missing = useMemo(
    () => (values ? unresolvedEmailTokens(`${subject} ${body}`, values) : []),
    [subject, body, values],
  );

  function pickTemplate(id: string) {
    setTemplateId(id);
    const template = templates?.find((t) => t.id === id);
    if (!template) return;
    setSubject(renderEmailTemplate(template.subject, values ?? {}));
    setBody(renderEmailTemplate(template.body, values ?? {}));
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      // The caller's files first: on a work order or a material order the
      // document is the point of the email, and an inbox lists them in order.
      const built = await Promise.all(
        documents.map(async (document) => ({
          filename: document.filename,
          mimeType: document.mimeType,
          content: await document.build(),
        })),
      );
      const attachments = [...built, ...toAttachments(files)];
      const result = await sendEmail({
        to,
        subject,
        text: body,
        jobId: jobId ?? null,
        contactId: contactId ?? null,
        internal,
        // Null when it is the caller's own, which is what the Edge Function
        // treats as the ordinary case.
        fromProfileId: sender?.profileId ?? null,
        ...(attachments.length ? { attachments } : {}),
      });
      setSentTo(result.from);
      await onSent?.({ to, from: result.from });
      onClose();
    } catch (e) {
      setError(
        e instanceof GmailError && e.needsConnection
          ? `${e.message} ${t("You can copy the email in the meantime.")}`
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      wide
      actions={
        <>
          <button
            className="btn-ghost"
            onClick={() => {
              void navigator.clipboard?.writeText(
                `To: ${to}\nSubject: ${subject}\n\n${body}`,
              );
              setCopied(true);
              void Promise.resolve(onCopied?.({ to })).catch((e: unknown) =>
                setError(e instanceof Error ? e.message : String(e)),
              );
            }}
          >
            {copied ? t("Copied!") : t("Copy email")}
          </button>
          {extraActions}
          <button
            className="btn-primary"
            disabled={busy || !sendingAs || Boolean(refusal) || !body.trim()}
            onClick={() => void send()}
          >
            {busy ? t("Sending…") : t(sendLabel)}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      {refusal && (
        <div className="err" role="alert" style={{ marginBottom: 8 }}>
          {t(refusal.message)}
        </div>
      )}

      {!sendingAs && (
        <div className="shelf" style={{ marginBottom: 8 }}>
          <b>{t("Gmail is not connected")}</b>
          {t(
            "Connect your Google account under Tools → Integrations and this sends itself. Until then, copy the email and send it from Gmail as before.",
          )}
        </div>
      )}

      {/* From. A select when there is more than one mailbox to choose between,
          and a line of text when there is not — a dropdown with one option in
          it is a control that does nothing. */}
      {sendingAs && (
        <div className="field full">
          <label htmlFor="compose-from">{t("From")}</label>
          {senders.length > 1 ? (
            <select
              id="compose-from"
              value={sender?.profileId ?? ""}
              onChange={(e) => {
                setFromProfileId(e.target.value);
                onFromChange?.(e.target.value);
              }}
            >
              {senders.map((row) => (
                <option key={row.profileId} value={row.profileId}>
                  {row.name
                    ? `${row.name} — ${row.emailAddress}`
                    : row.emailAddress}
                </option>
              ))}
            </select>
          ) : (
            <div className="na-hint">{sendingAs}</div>
          )}
        </div>
      )}

      {extraFields}

      {documents.length > 0 && (
        <div className="field full">
          <label>{t("Attached")}</label>
          {documents.map((document) => (
            <div className="na-hint" key={document.filename}>
              {document.filename}
              {document.label ? ` — ${document.label}` : ""}
            </div>
          ))}
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="field full">
          <label htmlFor="compose-template">{t("Template")}</label>
          <select
            id="compose-template"
            value={templateId}
            onChange={(e) => pickTemplate(e.target.value)}
          >
            <option value="">{t("Write it myself")}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
                {template.needs_review ? ` — ${t("needs review")}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* More than one person, when the job has more than one.
          Jonathon, 2026-09-01: "when we send emails to customers we need to be
          able to type out additional emails in the recipient field." A roof is
          bought by two people more often than one, and the second address —
          the spouse, the property manager, the adjuster on the claim — was a
          second send of the same letter done by hand, or was not done.

          One box rather than a To and a Cc. Everybody named gets the same
          letter and can see who else was on it, which is what a rep means by
          "add his wife"; a Cc would be the same thing with a distinction
          nobody in this office is drawing. */}
      <div className="field full">
        <label htmlFor="compose-to">{t("To")}</label>
        <input
          id="compose-to"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <div className="na-hint">
          {recipients.length > 1
            ? t("Goes to all {n}. Each of them sees the others.", {
                n: recipients.length,
              })
            : t("Add more people by separating the addresses with a comma.")}
        </div>
      </div>

      <div className="field full">
        <label htmlFor="compose-subject">{t("Subject")}</label>
        <input
          id="compose-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      <div className="field full">
        <label htmlFor="compose-body">{t("Message")}</label>
        <textarea
          id="compose-body"
          style={{ minHeight: 320 }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />

      {missing.length > 0 && (
        <div className="na-hint" style={{ marginTop: 4 }}>
          {tn(
            missing.length,
            "This template has 1 field Roof Board cannot fill: {list}. They will arrive looking exactly like that unless you edit them out.",
            "This template has {n} fields Roof Board cannot fill: {list}. They will arrive looking exactly like that unless you edit them out.",
            { list: missing.map((token) => `{{${token}}}`).join(", ") },
          )}
        </div>
      )}

      {sendingAs && !sentTo && (
        <div className="na-hint" style={{ marginTop: 8 }}>
          {/* "Replies come back to you" is only true when it is your own
              mailbox. Sending from Samuel's means Samuel gets the answer,
              which is the point — and is worth saying before it is sent
              rather than discovered when the customer replies. */}
          {t("Sends from {from}.", { from: sendingAs })}{" "}
          {sender && (!own || sender.profileId !== own.profileId)
            ? t(
                "Replies go to {name}, not to you, and it is signed with their Gmail signature.",
                { name: sender.name ?? sendingAs },
              )
            : t(
                "Replies come back to you, and your Gmail signature goes on the end.",
              )}
        </div>
      )}
    </Modal>
  );
}
